import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import Select from "react-select";

import ai from "../../helpers/axios";
import Loader from "../loader";

const timeBracketsOptions = [
  { value: "weekdayBusinessHours", label: "Weekday Business Hours (Monday–Friday, 9:00 AM – 5:30 PM ET)" },
];

const acceptanceTimeOptions = Array.from(
  { length: (120 - 15) / 5 + 1 },
  (_, i) => 15 + i * 5,
).map((minutes) => ({ value: minutes, label: `${minutes} minutes` }));

const LeadAcceptanceConfigContent = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [timeBrackets, setTimeBrackets] = useState("weekdayBusinessHours");
  const [acceptanceTime, setAcceptanceTime] = useState(15);

  useEffect(() => {
    document.title = "Lead Acceptance Configuration | evoJets HQ";
  }, []);

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await ai
        .auth(token)
        .get("/api/leadAcceptanceConfig/");

      if (response.data.success && response.data.data) {
        setTimeBrackets(response.data.data.timeBrackets);
        setAcceptanceTime(response.data.data.acceptanceTime);
      }
    } catch (error) {
      toast.error("Failed to load Lead Acceptance configuration");
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleAcceptanceTimeChange = useCallback(async ({ value: acceptanceTime }) => {
    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();
      const response = await ai
        .auth(token)
        .put("/api/leadAcceptanceConfig/", { acceptanceTime });

      if (response.data.success) {
        setAcceptanceTime(acceptanceTime);
        toast.success("Lead Acceptance configuration saved successfully");
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      console.error("Error saving Lead Acceptance config:", error);
      toast.error("Failed to save Lead Acceptance configuration");
    } finally {
      setIsSaving(false);
    }
  }, [getAccessTokenSilently]);

  return (
    <div className="container position-relative">
      <h1 style={{ paddingTop: "20px", textAlign: "center" }}>
        Lead Acceptance Configuration
      </h1>

      <div className="duty-status-table mt-20">
        <table className="w-100">
          <thead className="fm-l2-parent-table-head dynamic-accordion-table-header">
          <tr className="fm-l2-parent-table-head-row">
            <th className="text-align-left">Time Bracket</th>
            <th className="text-align-left">Acceptance Time</th>
          </tr>
          </thead>
          <tbody className="fm-l2-parent-table-body">
          {isLoading ? (
            <tr className="fm-l2-parent-table-row">
              <td colSpan={2}>
                <div className="position-absolute w-100 h-100 custom-loader-wrapper">
                  <Loader additionalClasses="position-absolute top-50 start-50 translate-middle"/>
                </div>
              </td>
            </tr>
          ) : (
            <tr>
              <td className="p-2">
                <Select
                  classNamePrefix="select"
                  value={timeBracketsOptions.find(({ value }) => value === timeBrackets)}
                  options={timeBracketsOptions}
                  placeholder="Select Time Bracket"
                />
              </td>
              <td className="p-2">
                <Select
                  classNamePrefix="select"
                  value={acceptanceTimeOptions.find(({ value }) => value === acceptanceTime)}
                  options={acceptanceTimeOptions}
                  placeholder="Select Acceptance Time"
                  onChange={handleAcceptanceTimeChange}
                />
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadAcceptanceConfigContent;
